import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { mergeMap, map, catchError, tap } from "rxjs/operators";
import {EMPTY, from, of} from "rxjs";
import { ExportRequestsActions } from "./export-requests.actions";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { LoaderActions } from "../../common/loader/loader.actions";
import { NavigationService } from '../../../shared/services';
import { FeedbackActions } from "../../common/feedback/feedback.actions";
import {ExportRequestMapper} from '../../../mappers/table/export-requests/export-request-mapper';
import {ExportResquestRepo} from '../../../repositories/private';
import {ExportRequestState} from '../../../../swagger-gen/web-secured/models';
import {DEFAULT_PAGE_SIZE, RouteQueryParam} from '../../../shared/constants';

export class ExportRequestsEffect {
  private includedStates = [
    ExportRequestState.CREATED,
    ExportRequestState.DONE,
    ExportRequestState.INITIATED,
    ExportRequestState.SENT,
    ExportRequestState.PROCESS,
    ExportRequestState.ERROR,
  ];

  private actions$ = inject(Actions);
  private exportRequestMapper = inject(ExportRequestMapper);
  private exportRequestRepo = inject(ExportResquestRepo);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private navigationService = inject(NavigationService);

  searchExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.search),
      tap(() => this.startLoader()),
      mergeMap((param) => {
        const _params = { ...param.filter };

        _params.body = {
          ..._params.body,
          states: this.includedStates,
        };

        return this.exportRequestRepo.searchExportRequests(_params).pipe(
          map((response) => ({
            paging: response.paging,
            list: this.exportRequestMapper.mapEntities(response.list ?? []),
          })),
          map((response) => ExportRequestsActions.fetchSuccess({ response })),
          tap(() => this.stopLoader()),
          catchError((error) =>
            of(
              ExportRequestsActions.fetchFailure({ message: error?.toString() })
            )
          )
        );
      })
    );
  });

  /**
   * Create Export request and associated ontologies
   */
  createExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.createRequest),
      tap(() => this.startLoader()),
      mergeMap((param) =>
        this.exportRequestRepo
          .createExportRequestAndOntologies({
            request: param.exportRequestParam,
            ontologies: param.ontologies,
            toExecute: param.toExecute,
          })
          .pipe(
            map(() => ExportRequestsActions.createRequestSuccess()),
            tap(() =>
              this.afterExportRequestSuccess(
                this.translate.instant(
                  "feedback.detail.export-request-create-success"
                )
              )
            ),
            catchError((error) => {
              const errorAction = of(
                ExportRequestsActions.createRequestFailure({
                  message: error?.toString(),
                })
              );
              this.afterExportRequestFailure(
                this.translate.instant(
                  "feedback.detail.export-request-create-error"
                )
              );

              return errorAction;
            })
          )
      )
    );
  });

  /**
   * Update an Export Request with associated Ontologies
   */
  updateExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.updateRequest),
      tap(() => this.startLoader()),
      mergeMap((param) =>
        this.exportRequestRepo
          .updateExportRequestAndOntologies({
            request: param.exportRequestParam,
            ontologies: param.ontologies,
            toExecute: param.toExecute,
          })
          .pipe(
            map(() => ExportRequestsActions.createRequestSuccess()),
            tap(() => {
              this.afterExportRequestSuccess(
                this.translate.instant(
                  "feedback.detail.export-request-update-success"
                )
              );
            }),
            catchError((error) => {
              const errorAction = of(
                ExportRequestsActions.createRequestFailure({
                  message: error?.toString(),
                })
              );
              this.afterExportRequestFailure(
                this.translate.instant(
                  "feedback.detail.export-request-update-error"
                )
              );
              return errorAction;
            })
          )
      )
    );
  });

  /**
   * Delete an Export Request
   */
  deleteExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.delete),
      mergeMap((param) =>
        this.exportRequestRepo
          .deleteExportRequest(param.exportRequestUid)
          .pipe(
            map(() => this.refresh()),
            tap(() =>
              this.afterExportRequestSuccess(
                this.translate.instant(
                  "feedback.detail.export-request-delete-success"
                )
              )
            ),
            catchError((error) => {
              this.afterExportRequestFailure(
                this.translate.instant(
                  "feedback.detail.export-request-delete-error"
                )
              );
              return of(
                ExportRequestsActions.fetchFailure({
                  message: error?.toString(),
                })
              );
            })
          )
      )
    );
  });

  /**
   * Clone an Export Request
   */
  cloneExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.clone),
      mergeMap((param) =>
        this.exportRequestRepo.cloneExportRequest(param.exportRequestUid).pipe(
          mergeMap((clonedExportRequestUid) =>
            this.exportRequestRepo.configuredExportRequestResponse(
              clonedExportRequestUid as string
            )
          ),
          map(() => this.refresh()),
          tap(() =>
            this.afterExportRequestSuccess(
              this.translate.instant(
                "feedback.detail.export-request-clone-success"
              )
            )
          ),
          catchError((error) => {
            this.store.dispatch(
              ExportRequestsActions.duplicateFailure({
                message: error?.toString(),
              })
            );
            this.afterExportRequestFailure(
              this.translate.instant(
                "feedback.detail.export-request-clone-error"
              )
            );
            return of(this.refresh());
          })
        )
      )
    );
  });

  /**
   * Duplicate an Export Request
   */
  duplicateExportRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportRequestsActions.duplicateRequest),
      mergeMap(({ exportRequestUid }) => {
        return this.exportRequestRepo
          .duplicateExportRequest(exportRequestUid)
          .pipe(
            mergeMap((newUid: string) => {
              const queryParams = {[RouteQueryParam.EXPORT_REQUEST_UID]: newUid};
              return from(this.navigationService.gotoCreationExportRequestPage(queryParams,null)
            ).pipe(
                map(() => {
                  this.stopLoader();
                  return FeedbackActions.success({ message: this.translate.instant("feedback.detail.export-request-duplicate-success") });
                }),
                catchError((error) => {
                  this.store.dispatch(ExportRequestsActions.fetchFailure({message: error?.toString()}));
                  this.afterExportRequestFailure(this.translate.instant("feedback.detail.export-request-duplicate-failure"));
                  return of(this.refresh());
                })
              );
            })
          );
      })
    )
  );

  /**
   * Retry an Export Request
   */
  retryExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.retry),
      mergeMap((param) => {
        return this.exportRequestRepo
          .retryExportRequest(param.exportRequestUid)
          .pipe(
            map(() => this.refresh()),
            tap(() =>
              this.afterExportRequestSuccess(
                this.translate.instant(
                  "feedback.detail.export-request-retry-success"
                )
              )
            ),
            catchError(() => {
              this.afterExportRequestFailure(
                this.translate.instant(
                  "feedback.detail.export-request-retry-error"
                )
              );
              return of(
                FeedbackActions.failure({ message: "Une erreur survenue" })
              );
            })
          );
      })
    );
  });

  /**
   * Run an Export Request
   */
  runExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.configured),
      mergeMap((param) =>
        this.exportRequestRepo
          .configuredExportRequestResponse(param.exportRequestUid)
          .pipe(
            map(() => {
              this.refresh();
              return ExportRequestsActions.createRequestOntologiesSuccess();
            }),
            tap(() =>
              this.afterExportRequestSuccess(
                this.translate.instant(
                  "feedback.detail.export-request-run-success"
                )
              )
            ),
            catchError((error) => {
              this.store.dispatch(
                ExportRequestsActions.fetchFailure({
                  message: error?.toString(),
                })
              );
              this.afterExportRequestFailure(
                this.translate.instant(
                  "feedback.detail.export-request-run-error"
                )
              );
              return of(this.refresh());
            })
          )
      )
    );
  });

  /**
   * Download an Export Request
   */
  downloadExportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportRequestsActions.download),
      mergeMap((param) =>
        this.exportRequestRepo
          .downloadExportRequest(param.exportRequestUid)
          .pipe(
            map((response) => {

              if (response) {
                this.downloadFile(response);

                return FeedbackActions.emptyResponse({});
              }

              return FeedbackActions.warning({
                summary: this.translate.instant('feedback.summary.noFileFoundForExportRequest') ,
                message: this.translate.instant(`feedback.detail.noFileFoundForExportRequest`)
              });
            }),
            catchError((error) => {
              this.store.dispatch(
                ExportRequestsActions.fetchFailure({
                  message: error?.toString(),
                })
              );
              return of(this.refresh());
            })
          )
      )
    );
  });

  downloadFile(param: { blob: Blob; fileName: string | undefined }): void {
    try {
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(param.blob);
      link.href = url;
      link.download = param.fileName || "exported-file";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  }

  private refresh() {
    return ExportRequestsActions.search({
      currentPage: 0,
      filter: { offset: 0, limit: DEFAULT_PAGE_SIZE },
    });
  }

  private startLoader() {
    this.store.dispatch(
      LoaderActions.start({
        message: this.translate.instant("loader.default-title"),
      })
    );
  }

  private stopLoader() {
    this.store.dispatch(LoaderActions.stop());
  }

  private afterExportRequestSuccess(detail: string) {
    this.stopLoader();
    this.store.dispatch(FeedbackActions.success({ message: detail }));
    this.navigationService.gotoExportRequestPage();
  }

  private afterExportRequestFailure(detail: string) {
    this.stopLoader();
    this.store.dispatch(FeedbackActions.failure({ message: detail }));
    this.navigationService.gotoExportRequestPage();
  }
}
